import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { avatarFor } from '@/lib/avatars';
import { cn } from '@/lib/cn';
import { initials as toInitials } from '@/lib/format';

/**
 * Deterministic avatar colour from the person's name, so the same person keeps
 * the same colour everywhere without storing one. Hues are drawn from the brand
 * family rather than random pastels.
 */
const PALETTE = [
  'bg-signal text-white',
  'bg-navy text-white',
  'bg-digital text-white',
  'bg-[#7C4D8B] text-white',
  'bg-[#B2532F] text-white',
  'bg-[#2F6E8B] text-white',
  'bg-[#8B5A2B] text-white',
  'bg-[#4A5D8A] text-white',
] as const;

function paletteFor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return PALETTE[hash % PALETTE.length] ?? PALETTE[0];
}

const SIZES = {
  xs: 'h-7 w-7 text-[11px]',
  sm: 'h-9 w-9 text-xs',
  md: 'h-10 w-10 text-[13px]',
  lg: 'h-14 w-14 text-lg',
  xl: 'h-[68px] w-[68px] text-2xl',
} as const;

/** The enlarged preview, in CSS pixels. Big enough to actually read a face. */
const PREVIEW = 208;
const GAP = 12;

interface PreviewPosition {
  left: number;
  top: number;
}

export function Avatar({
  name,
  src,
  size = 'md',
  className,
  /**
   * Force a colour, which also opts out of the illustration.
   *
   * Asking for a specific colour means the monogram is the point — the reset
   * password screen shows one for an email address, and a face there would be
   * claiming to know who that is.
   */
  tone,
  /** Suppress the hover preview where it would fight the surrounding UI. */
  preview = true,
}: {
  name: string;
  src?: string | null;
  size?: keyof typeof SIZES;
  className?: string;
  tone?: string;
  preview?: boolean;
}) {
  const label = toInitials(name);
  // A broken image must never leave a hole where a person should be.
  const [failed, setFailed] = useState(false);
  const [position, setPosition] = useState<PreviewPosition | null>(null);
  const anchor = useRef<HTMLSpanElement>(null);

  const image = failed || tone ? null : (src ?? avatarFor(name));

  /**
   * Place the preview beside the avatar, flipping and clamping so it never
   * leaves the viewport — a face half off the right edge of a wide table would
   * be worse than no preview at all.
   */
  const open = () => {
    const rect = anchor.current?.getBoundingClientRect();
    if (!rect) return;

    const spaceRight = window.innerWidth - rect.right;
    const left =
      spaceRight >= PREVIEW + GAP + 8
        ? rect.right + GAP
        : Math.max(8, rect.left - PREVIEW - GAP);

    const top = Math.max(
      8,
      Math.min(
        rect.top + rect.height / 2 - PREVIEW / 2,
        window.innerHeight - PREVIEW - 8,
      ),
    );

    setPosition({ left, top });
  };

  const close = () => setPosition(null);

  if (image) {
    return (
      <>
        <span
          ref={anchor}
          title={name}
          tabIndex={preview ? 0 : undefined}
          onMouseEnter={preview ? open : undefined}
          onMouseLeave={preview ? close : undefined}
          onFocus={preview ? open : undefined}
          onBlur={preview ? close : undefined}
          className={cn(
            // The circle clips, so the thumbnail can grow inside it without
            // pushing the row around — a table of twenty avatars must not
            // reflow on hover.
            'group relative inline-block shrink-0 overflow-hidden rounded-full bg-pale-blue',
            preview && 'cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal',
            SIZES[size],
            className,
          )}
        >
          <img
            src={image}
            alt=""
            loading="lazy"
            onError={() => setFailed(true)}
            className={cn(
              'h-full w-full object-cover',
              'transition-transform duration-200 ease-out will-change-transform',
              'group-hover:scale-[1.18] motion-reduce:transform-none motion-reduce:transition-none',
            )}
          />
        </span>

        {/*
          Portalled to the body so the preview is never clipped by a table's
          overflow or trapped under a sticky header.
        */}
        {position &&
          createPortal(
            <div
              role="tooltip"
              aria-hidden
              style={{ left: position.left, top: position.top, width: PREVIEW, height: PREVIEW }}
              className="pointer-events-none fixed z-[80] animate-fade-in overflow-hidden rounded-full border-4 border-white bg-pale-blue shadow-modal"
            >
              {/*
                The picture alone, in the same circle as the thumbnail. The name
                is already on the row the avatar sits in, so repeating it here
                only covers what the person hovered to see.
              */}
              <img src={image} alt="" className="block h-full w-full object-cover" />
            </div>,
            document.body,
          )}
      </>
    );
  }

  return (
    <span
      aria-hidden
      title={name}
      className={cn(
        'inline-flex shrink-0 select-none items-center justify-center rounded-full font-semibold',
        SIZES[size],
        tone ?? paletteFor(name),
        className,
      )}
    >
      {label}
    </span>
  );
}
